import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Calendar as CalendarIcon, MapPin, Clock, ChevronLeft, ChevronRight,
  ArrowLeft, Download, Loader2, CalendarDays, ClipboardList, UserX, X, ChevronDown,
  CreditCard, Search, Filter, Tag, ExternalLink,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const CURRENCY_SYMBOLS = { usd: '$', gbp: '£', eur: '€', aud: 'A$' };

const stripHtml = (html) => (html || '').replace(/<[^>]+>/g, '').trim();

// Format a JS Date to Google Calendar / Outlook Web date string (YYYYMMDDTHHmmssZ)
const toCalDate = (dateStr, allDay = false) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d)) return '';
  if (allDay) {
    const pad = n => String(n).padStart(2, '0');
    return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`;
  }
  return d.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
};

// Build a Google Calendar "Add event" URL
const googleCalUrl = (ev) => {
  const start = toCalDate(ev.start, ev.all_day);
  const end   = toCalDate(ev.end || ev.start, ev.all_day);
  const dates = ev.all_day ? `${start}/${end}` : `${start}/${end}`;
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: ev.title || '',
    dates,
    details: stripHtml(ev.description_html) || '',
    location: ev.location || '',
  });
  return `https://calendar.google.com/calendar/render?${params}`;
};

// Build an Outlook Web "Add event" URL
const outlookWebUrl = (ev) => {
  const params = new URLSearchParams({
    path: '/calendar/action/compose',
    rru: 'addevent',
    startdt: ev.start ? new Date(ev.start).toISOString() : '',
    enddt: (ev.end || ev.start) ? new Date(ev.end || ev.start).toISOString() : '',
    subject: ev.title || '',
    body: stripHtml(ev.description_html) || '',
    location: ev.location || '',
  });
  return `https://outlook.live.com/calendar/0/deeplink/compose?${params}`;
};

// "Add to Calendar" dropdown — shows Google, Apple/Outlook (.ics), and Outlook Web
function AddToCalendarButton({ ev, icsUrl }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const ref = useRef(null);
  const btnRef = useRef(null);
  const dropRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (
        ref.current && !ref.current.contains(e.target) &&
        dropRef.current && !dropRef.current.contains(e.target)
      ) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleToggle = () => {
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      const DROPDOWN_H = 168; // 3 items × ~48px + padding
      const flipUp = (window.innerHeight - r.bottom) < DROPDOWN_H + 10;
      setPos({
        top: flipUp ? r.top - DROPDOWN_H - 6 : r.bottom + 6,
        left: r.left,
      });
    }
    setOpen(o => !o);
  };

  const dropdown = open && createPortal(
    <div
      ref={dropRef}
      style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 9999 }}
      className="w-52 bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden animate-fadeIn"
    >
          <a
            href={googleCalUrl(ev)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="event-add-google-cal"
            onClick={() => setOpen(false)}
            className="flex items-center gap-3 px-4 py-3 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
          >
            <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none">
              <path d="M22 12c0-5.52-4.48-10-10-10S2 6.48 2 12s4.48 10 10 10 10-4.48 10-10z" fill="#4285F4"/>
              <path d="M12 6.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11z" fill="white"/>
              <path d="M12 8a4 4 0 110 8 4 4 0 010-8z" fill="#4285F4"/>
              <path d="M12 10.5a1.5 1.5 0 100 3 1.5 1.5 0 000-3z" fill="white"/>
            </svg>
            Google Calendar
          </a>

          <a
            href={icsUrl}
            download
            data-testid="event-add-ics"
            onClick={() => setOpen(false)}
            className="flex items-center gap-3 px-4 py-3 text-sm text-slate-700 hover:bg-slate-50 transition-colors border-t border-slate-100"
          >
            <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none">
              <rect x="3" y="4" width="18" height="18" rx="2" stroke="#6B7280" strokeWidth="1.5" fill="none"/>
              <path d="M16 2v4M8 2v4M3 10h18" stroke="#6B7280" strokeWidth="1.5" strokeLinecap="round"/>
              <path d="M8 15h8M8 18h5" stroke="#6B7280" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
            Apple / Outlook (.ics)
          </a>

          <a
            href={outlookWebUrl(ev)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="event-add-outlook-web"
            onClick={() => setOpen(false)}
            className="flex items-center gap-3 px-4 py-3 text-sm text-slate-700 hover:bg-slate-50 transition-colors border-t border-slate-100"
          >
            <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none">
              <rect x="2" y="4" width="20" height="16" rx="2" fill="#0078D4"/>
              <path d="M2 8l10 6 10-6" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
            Outlook Web
          </a>
        </div>,
    document.body
  );

  return (
    <div className="relative inline-block" ref={ref}>
      <button
        ref={btnRef}
        onClick={handleToggle}
        data-testid="event-add-to-calendar-btn"
        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 transition-colors"
      >
        <CalendarIcon className="w-4 h-4" />
        Add to calendar
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {dropdown}
    </div>
  );
}

const PublicEventsPage = () => {
  const { siteSlug, eventId, cancelToken, waitlistToken } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewDate, setViewDate] = useState(new Date());
  const [mode, setMode] = useState(() => {
    const p = new URLSearchParams(window.location.search);
    const v = p.get('view');
    return v === 'list' ? 'list' : 'calendar';
  }); // calendar | list

  // Embed mode — parsed once from URL params
  const embedParams = useMemo(() => {
    const p = new URLSearchParams(window.location.search);
    const isEmbed = p.get('embed') === '1';
    return {
      isEmbed,
      showFilters: isEmbed ? p.get('show_filters') !== '0' : true,
      showIcal:   isEmbed ? p.get('show_ical')    !== '0' : true,
      showAbsence: isEmbed ? p.get('show_absence') !== '0' : true,
    };
  }, []);
  const [absOpen, setAbsOpen] = useState(false);
  const [absForm, setAbsForm] = useState({ student_name: '', parent_name: '', parent_email: '', date_from: '', date_to: '', reason: '', notes: '' });
  const [absSubmitting, setAbsSubmitting] = useState(false);
  const [absDone, setAbsDone] = useState(false);

  // ---------- Filters ----------
  const FILTER_KEY = `nexus_cal_filter_${siteSlug}`;
  const loadSavedFilters = () => {
    try { return JSON.parse(localStorage.getItem(FILTER_KEY) || '{}'); } catch { return {}; }
  };
  const [filterType, setFilterType] = useState(() => loadSavedFilters().filterType || '');
  const [filterDept, setFilterDept] = useState(() => loadSavedFilters().filterDept || '');
  const [filterSearch, setFilterSearch] = useState(() => loadSavedFilters().filterSearch || '');
  const [filterSource, setFilterSource] = useState(() => loadSavedFilters().filterSource || '');

  // Persist filters
  useEffect(() => {
    const saved = { filterType, filterDept, filterSearch, filterSource };
    if (filterType || filterDept || filterSearch || filterSource) {
      localStorage.setItem(FILTER_KEY, JSON.stringify(saved));
    } else {
      localStorage.removeItem(FILTER_KEY);
    }
  }, [filterType, filterDept, filterSearch, filterSource, FILTER_KEY]);

  // RSVP (detail page)
  const [rsvpForm, setRsvpForm] = useState({ name: '', email: '', party_size: 1 });
  const [rsvpSubmitting, setRsvpSubmitting] = useState(false);
  const [rsvpResult, setRsvpResult] = useState(null);
  const [rsvpError, setRsvpError] = useState(null);
  // Cancel view
  const [cancelData, setCancelData] = useState(null);
  const [cancelDone, setCancelDone] = useState(false);
  // Waitlist (detail page + claim view)
  const [wlForm, setWlForm] = useState({ name: '', email: '', party_size: 1 });
  const [wlResult, setWlResult] = useState(null);
  const [wlError, setWlError] = useState(null);
  const [wlSubmitting, setWlSubmitting] = useState(false);
  const [claimData, setClaimData] = useState(null);
  const [claimResult, setClaimResult] = useState(null);
  const [claimError, setClaimError] = useState(null);
  const [claiming, setClaiming] = useState(false);

  // Ticketing
  const [ticketForm, setTicketForm] = useState({ buyer_name: '', buyer_email: '', quantity: 1 });
  const [ticketSubmitting, setTicketSubmitting] = useState(false);
  const [ticketError, setTicketError] = useState(null);
  const [ticketSuccess, setTicketSuccess] = useState(false);
  const [ticketCancelled, setTicketCancelled] = useState(false);
  const [ticketSessionId, setTicketSessionId] = useState(null);
  const [ticketQrB64, setTicketQrB64] = useState(null);
  const [ticketQty, setTicketQty] = useState(1);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      if (waitlistToken) {
        const res = await fetch(`${API_URL}/api/public/events/${siteSlug}/waitlist/${waitlistToken}`);
        if (!res.ok) throw new Error('Offer not found');
        setClaimData(await res.json());
      } else if (cancelToken) {
        const res = await fetch(`${API_URL}/api/public/events/${siteSlug}/rsvp/${cancelToken}`);
        if (!res.ok) throw new Error('RSVP not found');
        setCancelData(await res.json());
      } else if (eventId) {
        const res = await fetch(`${API_URL}/api/public/events/${siteSlug}/${eventId}`);
        if (!res.ok) throw new Error('Event not found');
        setDetail(await res.json());
      } else {
        const res = await fetch(`${API_URL}/api/public/events/${siteSlug}`);
        if (!res.ok) throw new Error('Site not found');
        const json = await res.json();
        setData(json);
        setDetail(null);
        // Default the calendar to the month of the next upcoming event (fallback: latest)
        const evs = json.events || [];
        if (evs.length) {
          const now = Date.now();
          const upcoming = evs.filter(e => e.start && new Date(e.start).getTime() >= now)
            .sort((a, b) => new Date(a.start) - new Date(b.start))[0];
          const target = upcoming || evs[evs.length - 1];
          if (target?.start) setViewDate(new Date(target.start));
        }
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [siteSlug, eventId, cancelToken, waitlistToken]);

  const submitWaitlist = async () => {
    if (!wlForm.name.trim() || !wlForm.email.trim()) { setWlError('Please enter your name and email.'); return; }
    setWlSubmitting(true); setWlError(null);
    try {
      const res = await fetch(`${API_URL}/api/public/events/${siteSlug}/${eventId}/waitlist`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...wlForm, party_size: parseInt(wlForm.party_size, 10) || 1 }),
      });
      const json = await res.json();
      if (!res.ok) { setWlError(json.detail || 'Could not join the waitlist'); return; }
      setWlResult(json);
    } catch (e) {
      setWlError('Something went wrong. Please try again.');
    } finally {
      setWlSubmitting(false);
    }
  };

  const claimSpot = async () => {
    setClaiming(true); setClaimError(null);
    try {
      const res = await fetch(`${API_URL}/api/public/events/${siteSlug}/waitlist/${waitlistToken}/claim`, { method: 'POST' });
      const json = await res.json();
      if (!res.ok) { setClaimError(json.detail || 'Could not claim this spot'); return; }
      setClaimResult(json);
    } catch (e) {
      setClaimError('Something went wrong. Please try again.');
    } finally {
      setClaiming(false);
    }
  };

  const submitRsvp = async () => {
    if (!rsvpForm.name.trim() || !rsvpForm.email.trim()) {
      alert('Please enter your name and email.');
      return;
    }
    setRsvpSubmitting(true);
    setRsvpError(null);
    try {
      const res = await fetch(`${API_URL}/api/public/events/${siteSlug}/${eventId}/rsvp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...rsvpForm, party_size: parseInt(rsvpForm.party_size, 10) || 1 }),
      });
      const json = await res.json();
      if (!res.ok) { setRsvpError(json.detail || 'Could not register'); return; }
      setRsvpResult(json);
      load();
    } catch (e) {
      setRsvpError('Sorry, registration failed. Please try again.');
    } finally {
      setRsvpSubmitting(false);
    }
  };

  const doCancelRsvp = async () => {
    try {
      const res = await fetch(`${API_URL}/api/public/events/${siteSlug}/rsvp/${cancelToken}/cancel`, { method: 'POST' });
      if (!res.ok) throw new Error();
      setCancelDone(true);
    } catch { alert('Could not cancel. Please try again.'); }
  };

  const buyTickets = async () => {
    if (!ticketForm.buyer_name.trim() || !ticketForm.buyer_email.trim()) {
      setTicketError('Please enter your name and email address.');
      return;
    }
    setTicketSubmitting(true);
    setTicketError(null);
    try {
      const res = await fetch(`${API_URL}/api/public/event-payments/${siteSlug}/events/${eventId}/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          buyer_name: ticketForm.buyer_name.trim(),
          buyer_email: ticketForm.buyer_email.trim(),
          quantity: parseInt(ticketForm.quantity, 10) || 1,
          origin_url: window.location.origin,
        }),
      });
      const json = await res.json();
      if (!res.ok) { setTicketError(json.detail || 'Could not create checkout. Please try again.'); return; }
      window.location.href = json.checkout_url;
    } catch {
      setTicketError('Something went wrong. Please try again.');
    } finally {
      setTicketSubmitting(false);
    }
  };

  const submitAbsence = async () => {
    if (!absForm.student_name.trim() || !absForm.date_from || !absForm.reason.trim()) {
      alert('Please fill in the student name, start date and reason.');
      return;
    }
    setAbsSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/public/events/${siteSlug}/absentee`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(absForm),
      });
      if (!res.ok) throw new Error('Submission failed');
      setAbsDone(true);
    } catch (e) {
      alert('Sorry, we could not submit your report. Please try again.');
    } finally {
      setAbsSubmitting(false);
    }
  };

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setRsvpResult(null); setRsvpError(null); setRsvpForm({ name: '', email: '', party_size: 1 }); setWlResult(null); setWlError(null); setWlForm({ name: '', email: '', party_size: 1 }); }, [eventId]);

  // Handle Stripe return URLs: ?ticket_session=... or ?ticket_cancelled=1
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const session = params.get('ticket_session');
    const cancelled = params.get('ticket_cancelled');
    if (cancelled) { setTicketCancelled(true); return; }
    if (!session) return;
    setTicketSessionId(session);
    // Poll payment status
    const poll = async () => {
      try {
        const res = await fetch(`${API_URL}/api/public/event-payments/status/${session}`);
        if (res.ok) {
          const data = await res.json();
          if (data.payment_status === 'paid') {
            setTicketSuccess(true);
            setTicketQty(data.quantity || 1);
            if (data.qr_code_b64) setTicketQrB64(data.qr_code_b64);
          }
        }
      } catch {}
    };
    poll();
    // Clean up URL so refresh doesn't re-trigger
    const url = new URL(window.location.href);
    url.searchParams.delete('ticket_session');
    url.searchParams.delete('ticket_cancelled');
    window.history.replaceState({}, '', url.toString());
  }, []);

  const events = data?.events || [];
  const tenantName = data?.tenant_name || detail?.tenant_name || 'Events';
  const filterOptions = data?.filter_options || { event_types: [], departments: [], tags: [] };
  const icalSources = data?.ical_sources || [];

  // Apply client-side filters
  const filteredEvents = useMemo(() => {
    let evs = events;
    if (filterSource) {
      evs = evs.filter(e => e.ical_source_id === filterSource);
    }
    if (filterType) {
      const ft = filterType.toLowerCase();
      evs = evs.filter(e => (e.event_type || '').toLowerCase() === ft);
    }
    if (filterDept) {
      const fd = filterDept.toLowerCase();
      evs = evs.filter(e => (e.departments || []).some(d => d.toLowerCase() === fd));
    }
    if (filterSearch) {
      const fs = filterSearch.toLowerCase();
      evs = evs.filter(e =>
        (e.title || '').toLowerCase().includes(fs) ||
        (e.location || '').toLowerCase().includes(fs) ||
        (e.event_type || '').toLowerCase().includes(fs) ||
        (e.departments || []).some(d => d.toLowerCase().includes(fs)) ||
        (e.tags || []).some(t => t.toLowerCase().includes(fs))
      );
    }
    return evs;
  }, [events, filterType, filterDept, filterSearch, filterSource]);

  const activeFilterCount = [filterType, filterDept, filterSearch, filterSource].filter(Boolean).length;

  const calendarCells = useMemo(() => {
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();
    const startDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startDay; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [viewDate]);

  const eventsByDay = useMemo(() => {
    const map = {};
    filteredEvents.forEach(ev => {
      if (!ev.start) return;
      const start = new Date(ev.start);
      const end = ev.end ? new Date(ev.end) : start;
      const s = new Date(start.getFullYear(), start.getMonth(), start.getDate());
      const e = new Date(end.getFullYear(), end.getMonth(), end.getDate());
      for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
        const key = new Date(d).toDateString();
        (map[key] = map[key] || []).push(ev);
      }
    });
    return map;
  }, [filteredEvents]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="w-8 h-8 animate-spin text-indigo-600" /></div>;
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 text-center px-4">
        <CalendarIcon className="w-12 h-12 text-slate-300 mb-4" />
        <h1 className="text-2xl font-bold text-slate-800">{error}</h1>
        <Link to={`/site/${siteSlug}`} className="text-indigo-600 mt-3 hover:underline">Go to site</Link>
      </div>
    );
  }

  // ---------- WAITLIST CLAIM VIEW ----------
  if (waitlistToken) {
    const ev = claimData?.event;
    const entry = claimData?.entry;
    const expired = claimData?.expired || (entry && entry.status === 'expired');
    const alreadyClaimed = entry && entry.status === 'claimed';
    return (
      <div className="min-h-screen bg-slate-50">
        <Helmet><title>Claim your spot — {tenantName}</title></Helmet>
        <div className="max-w-md mx-auto px-4 py-16">
          <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center shadow-sm" data-testid="waitlist-claim-card">
            {claimResult || alreadyClaimed ? (
              <>
                <div className="w-14 h-14 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto mb-4"><CalendarDays className="w-7 h-7" /></div>
                <h1 className="text-xl font-bold text-slate-900">You're in! 🎉</h1>
                <p className="text-sm text-slate-500 mt-1">Your spot for {ev?.title} is confirmed. We've emailed the details.</p>
              </>
            ) : expired ? (
              <>
                <div className="w-14 h-14 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center mx-auto mb-4"><Clock className="w-7 h-7" /></div>
                <h1 className="text-xl font-bold text-slate-900">This offer has expired</h1>
                <p className="text-sm text-slate-500 mt-1">The spot has been offered to the next person on the waitlist.</p>
              </>
            ) : (
              <>
                <h1 className="text-xl font-bold text-slate-900">A spot opened up!</h1>
                <p className="text-sm text-slate-500 mt-2">
                  {entry?.name}, a place is available for <strong>{ev?.title}</strong>
                  {ev?.start ? ` on ${new Date(ev.start).toLocaleString()}` : ''} ({entry?.party_size} place(s)).
                </p>
                {claimError && <div className="mt-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-3 py-2" data-testid="waitlist-claim-error">{claimError}</div>}
                <button onClick={claimSpot} disabled={claiming} className="mt-6 w-full py-2.5 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-60" data-testid="waitlist-claim-btn">{claiming ? 'Claiming…' : 'Claim my spot'}</button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ---------- RSVP CANCEL VIEW ----------
  if (cancelToken) {
    const ev = cancelData?.event;
    const rsvp = cancelData?.rsvp;
    const alreadyCancelled = rsvp?.status === 'cancelled';
    return (
      <div className="min-h-screen bg-slate-50">
        <Helmet><title>Cancel RSVP — {tenantName}</title></Helmet>
        <div className="max-w-md mx-auto px-4 py-16">
          <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center shadow-sm" data-testid="rsvp-cancel-card">
            {cancelDone || alreadyCancelled ? (
              <>
                <div className="w-14 h-14 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center mx-auto mb-4"><X className="w-7 h-7" /></div>
                <h1 className="text-xl font-bold text-slate-900">RSVP cancelled</h1>
                <p className="text-sm text-slate-500 mt-1">Your spot for {ev?.title} has been released. Thanks for letting us know.</p>
              </>
            ) : (
              <>
                <h1 className="text-xl font-bold text-slate-900">Cancel your RSVP?</h1>
                <p className="text-sm text-slate-500 mt-2">
                  {rsvp?.name}, you're registered for <strong>{ev?.title}</strong>
                  {ev?.start ? ` on ${new Date(ev.start).toLocaleString()}` : ''} ({rsvp?.party_size} seat(s)).
                </p>
                <button onClick={doCancelRsvp} className="mt-6 w-full py-2.5 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700" data-testid="rsvp-cancel-confirm-btn">Yes, cancel my RSVP</button>
                <button onClick={() => navigate(`/site/${siteSlug}/events/${ev?.event_id}`)} className="mt-2 w-full py-2.5 rounded-lg border border-slate-200 text-sm text-slate-600" data-testid="rsvp-cancel-keep-btn">Keep my RSVP</button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ---------- EVENT DETAIL ----------
  if (eventId && detail?.event) {
    const ev = detail.event;
    const icsUrl = `${API_URL}/api/public/events/${siteSlug}/${ev.event_id}.ics`;
    return (
      <div className="min-h-screen bg-slate-50">
        <Helmet><title>{ev.title} — {tenantName}</title></Helmet>
        <div className="max-w-3xl mx-auto px-4 py-10">
          <button onClick={() => navigate(`/site/${siteSlug}/events`)} className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 mb-6" data-testid="event-back-btn">
            <ArrowLeft className="w-4 h-4" /> Back to calendar
          </button>
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
            <div className="h-2" style={{ backgroundColor: ev.color || '#6366f1' }} />
            <div className="p-8">
              {ev.category && <span className="text-xs font-semibold uppercase tracking-wide text-indigo-600">{ev.category}</span>}
              <h1 className="text-3xl font-bold text-slate-900 mt-1">{ev.title}</h1>
              {/* Event type & department & calendar source badges */}
              {(ev.event_type || (ev.departments || []).length > 0 || ev.ical_source_name) && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {ev.ical_source_name && (
                    <span className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full text-white font-medium" style={{ backgroundColor: ev.color || '#6366f1' }}>
                      {ev.ical_source_name}
                    </span>
                  )}
                  {ev.event_type && (
                    <span className="inline-flex items-center gap-1 bg-slate-100 text-slate-600 text-xs px-2.5 py-1 rounded-full">
                      <Tag className="w-3 h-3" /> {ev.event_type}
                    </span>
                  )}
                  {(ev.departments || []).map(d => (
                    <span key={d} className="bg-indigo-50 text-indigo-700 text-xs px-2.5 py-1 rounded-full">{d}</span>
                  ))}
                </div>
              )}
              <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-600 mt-4">
                <span className="flex items-center gap-2"><Clock className="w-4 h-4 text-slate-400" />
                  {ev.all_day ? new Date(ev.start).toLocaleDateString() : new Date(ev.start).toLocaleString()}
                  {ev.end && !ev.all_day ? ` – ${new Date(ev.end).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : ''}
                </span>
                {ev.location && <span className="flex items-center gap-2"><MapPin className="w-4 h-4 text-slate-400" />{ev.location}</span>}
                {ev.cost != null && ev.cost > 0 && <span className="font-medium text-slate-700">${ev.cost}</span>}
              </div>
              {ev.description_html && (
                <div className="prose prose-slate max-w-none mt-6" dangerouslySetInnerHTML={{ __html: ev.description_html }} />
              )}
              <div className="flex flex-wrap gap-3 mt-8">
                <AddToCalendarButton ev={ev} icsUrl={icsUrl} />
                {ev.form && ev.form.status === 'active' && (
                  <a href={`/site/${siteSlug}`} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50" data-testid="event-form-link">
                    <ClipboardList className="w-4 h-4" /> {ev.form.name || 'Register / Consent form'}
                  </a>
                )}
                {ev.external_link_url && (
                  <a
                    href={ev.external_link_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800 text-white text-sm font-medium hover:bg-slate-700"
                    data-testid="event-external-link"
                  >
                    <ExternalLink className="w-4 h-4" /> Register / More info
                  </a>
                )}
              </div>

              {ev.rsvp_enabled && (
                <div className="mt-8 border-t border-slate-100 pt-6" data-testid="rsvp-section">
                  <div className="flex items-center justify-between mb-3">
                    <h2 className="text-lg font-semibold text-slate-900">Register / RSVP</h2>
                    {ev.spots_left != null && (
                      <span className={`text-sm font-medium ${ev.rsvp_full ? 'text-red-600' : 'text-emerald-600'}`} data-testid="rsvp-spots-left">
                        {ev.rsvp_full ? 'Fully booked' : `${ev.spots_left} spot${ev.spots_left === 1 ? '' : 's'} left`}
                      </span>
                    )}
                  </div>
                  {rsvpResult ? (
                    <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-5 text-center" data-testid="rsvp-success">
                      <p className="font-semibold text-emerald-800">You're registered!</p>
                      <p className="text-sm text-emerald-700 mt-1">A confirmation has been emailed to {rsvpForm.email}.</p>
                      {rsvpResult.cancel_token && (
                        <p className="text-sm text-emerald-700 mt-2">
                          Can't make it later?{' '}
                          <a href={`/site/${siteSlug}/events/rsvp/${rsvpResult.cancel_token}`} className="underline font-medium" data-testid="rsvp-cancel-link">Cancel your RSVP</a>.
                        </p>
                      )}
                      <div className="mt-4 flex justify-center">
                        <AddToCalendarButton ev={ev} icsUrl={icsUrl} />
                      </div>
                    </div>
                  ) : ev.rsvp_full ? (
                    ev.waitlist_enabled ? (
                      wlResult ? (
                        <div className="bg-amber-50 border border-amber-100 rounded-xl p-5 text-center" data-testid="waitlist-success">
                          <p className="font-semibold text-amber-800">You're on the waitlist ✓</p>
                          <p className="text-sm text-amber-700 mt-1">If a spot opens up we'll email you a link to claim it{wlResult.position ? ` — you're #${wlResult.position} in line` : ''}.</p>
                        </div>
                      ) : ev.waitlist_full ? (
                        <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 text-center text-slate-500 text-sm">This event is full and the waitlist is also full.</div>
                      ) : (
                        <div className="bg-amber-50/60 border border-amber-100 rounded-xl p-5 space-y-3" data-testid="waitlist-form">
                          <p className="text-sm font-medium text-amber-900">This event is full — join the waitlist and we'll email you if a spot opens up.</p>
                          {wlError && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-3 py-2" data-testid="waitlist-error">{wlError}</div>}
                          <div className="grid sm:grid-cols-2 gap-3">
                            <input value={wlForm.name} onChange={(e) => setWlForm(f => ({ ...f, name: e.target.value }))} placeholder="Your name" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="waitlist-name-input" />
                            <input type="email" value={wlForm.email} onChange={(e) => setWlForm(f => ({ ...f, email: e.target.value }))} placeholder="Email" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="waitlist-email-input" />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-slate-600 mb-1">Party size</label>
                            <input type="number" min="1" value={wlForm.party_size} onChange={(e) => setWlForm(f => ({ ...f, party_size: e.target.value }))} className="w-24 border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="waitlist-party-input" />
                          </div>
                          <button onClick={submitWaitlist} disabled={wlSubmitting} className="px-5 py-2.5 rounded-lg bg-amber-600 text-white text-sm font-medium hover:bg-amber-700 disabled:opacity-60" data-testid="waitlist-submit-btn">{wlSubmitting ? 'Joining…' : 'Join the waitlist'}</button>
                        </div>
                      )
                    ) : (
                      <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 text-center text-slate-500 text-sm">This event is fully booked.</div>
                    )
                  ) : (
                    <div className="bg-slate-50 rounded-xl p-5 space-y-3">
                      {rsvpError && (
                        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-3 py-2" data-testid="rsvp-error">{rsvpError}</div>
                      )}
                      <div className="grid sm:grid-cols-2 gap-3">
                        <div>
                          <label className="text-sm font-medium text-slate-700">Your name</label>
                          <input value={rsvpForm.name} onChange={(e) => setRsvpForm(f => ({ ...f, name: e.target.value }))} className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="rsvp-name-input" />
                        </div>
                        <div>
                          <label className="text-sm font-medium text-slate-700">Email</label>
                          <input type="email" value={rsvpForm.email} onChange={(e) => setRsvpForm(f => ({ ...f, email: e.target.value }))} className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="rsvp-email-input" />
                        </div>
                      </div>
                      <div className="w-28">
                        <label className="text-sm font-medium text-slate-700">Party size</label>
                        <input type="number" min="1" max={ev.spots_left != null ? ev.spots_left : undefined} value={rsvpForm.party_size} onChange={(e) => setRsvpForm(f => ({ ...f, party_size: e.target.value }))} className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="rsvp-party-input" />
                      </div>
                      <button onClick={submitRsvp} disabled={rsvpSubmitting} className="px-5 py-2.5 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-60" data-testid="rsvp-submit-btn">
                        {rsvpSubmitting ? 'Registering…' : 'Register'}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* BUY TICKETS */}
              {ev.ticket_enabled && ev.ticket_price && (
                <div className="mt-8 border-t border-slate-100 pt-6" data-testid="tickets-section">
                  <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-indigo-500" /> Get Tickets
                  </h2>
                  {ticketSuccess ? (
                    <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-6" data-testid="ticket-success">
                      <div className="flex flex-col items-center text-center mb-5">
                        <div className="w-12 h-12 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mb-3">
                          <CreditCard className="w-6 h-6" />
                        </div>
                        <p className="font-semibold text-emerald-800 text-lg">Payment confirmed!</p>
                        <p className="text-sm text-emerald-700 mt-1">
                          Your {ticketQty > 1 ? `${ticketQty} tickets` : 'ticket'} {ticketQty > 1 ? 'have' : 'has'} been confirmed. A receipt has been emailed to you.
                        </p>
                      </div>

                      {/* QR Code */}
                      {ticketQrB64 && (
                        <div className="border-t border-emerald-100 pt-5 mb-5" data-testid="ticket-qr-section">
                          <p className="text-xs text-emerald-700 font-semibold text-center mb-3 uppercase tracking-wider">
                            Show this at the door
                          </p>
                          <div className="flex flex-col items-center gap-3">
                            <div className="bg-white rounded-xl p-3 shadow-sm border border-emerald-100">
                              <img
                                src={ticketQrB64}
                                alt="Ticket QR Code"
                                className="w-48 h-48 block"
                                data-testid="ticket-qr-image"
                              />
                            </div>
                            <p className="text-xs text-emerald-600">Staff will scan this to verify your entry</p>
                            <a
                              href={ticketQrB64}
                              download={`ticket-qr-${ev.event_id?.slice(-6)}.png`}
                              className="text-xs text-emerald-700 underline underline-offset-2 hover:text-emerald-900"
                              data-testid="ticket-qr-download"
                            >
                              Download QR code
                            </a>
                          </div>
                        </div>
                      )}

                      <div className="border-t border-emerald-100 pt-4">
                        <p className="text-xs text-emerald-700 font-medium text-center mb-3">Add this event to your calendar</p>
                        <div className="flex justify-center" data-testid="ticket-success-add-to-calendar">
                          <AddToCalendarButton ev={ev} icsUrl={icsUrl} />
                        </div>
                      </div>
                    </div>
                  ) : ticketCancelled ? (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 text-center mb-4" data-testid="ticket-cancelled">
                      <p className="font-medium text-amber-800">Payment was cancelled — you can try again below.</p>
                    </div>
                  ) : null}
                  {!ticketSuccess && (
                    <div className="bg-slate-50 rounded-xl p-5 space-y-4">
                      <div className="flex items-center justify-between pb-3 border-b border-slate-200">
                        <span className="font-medium text-slate-700">{ev.ticket_name || 'General Admission'}</span>
                        <span className="text-xl font-bold text-slate-900">
                          {CURRENCY_SYMBOLS[ev.ticket_currency || 'usd']}{Number(ev.ticket_price).toFixed(2)}
                        </span>
                      </div>
                      {ticketError && (
                        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-3 py-2" data-testid="ticket-error">{ticketError}</div>
                      )}
                      <div className="grid sm:grid-cols-2 gap-3">
                        <div>
                          <label className="text-sm font-medium text-slate-700">Your name</label>
                          <input
                            value={ticketForm.buyer_name}
                            onChange={(e) => setTicketForm(f => ({ ...f, buyer_name: e.target.value }))}
                            className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                            data-testid="ticket-name-input"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium text-slate-700">Email for receipt</label>
                          <input
                            type="email"
                            value={ticketForm.buyer_email}
                            onChange={(e) => setTicketForm(f => ({ ...f, buyer_email: e.target.value }))}
                            className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                            data-testid="ticket-email-input"
                          />
                        </div>
                      </div>
                      <div className="flex items-end justify-between gap-4">
                        <div className="w-28">
                          <label className="text-sm font-medium text-slate-700">Quantity</label>
                          <input
                            type="number" min="1" max={ev.ticket_limit_per_order || 10}
                            value={ticketForm.quantity}
                            onChange={(e) => setTicketForm(f => ({ ...f, quantity: e.target.value }))}
                            className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                            data-testid="ticket-qty-input"
                          />
                        </div>
                        <div className="text-right">
                          <span className="text-sm text-slate-500 block mb-2">
                            Total: <strong className="text-slate-800">
                              {CURRENCY_SYMBOLS[ev.ticket_currency || 'usd']}{(Number(ev.ticket_price) * (parseInt(ticketForm.quantity, 10) || 1)).toFixed(2)}
                            </strong>
                          </span>
                          <button
                            onClick={buyTickets}
                            disabled={ticketSubmitting}
                            className="px-6 py-2.5 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-60 flex items-center gap-2"
                            data-testid="ticket-buy-btn"
                          >
                            <CreditCard className="w-4 h-4" />
                            {ticketSubmitting ? 'Preparing…' : 'Buy Tickets'}
                          </button>
                        </div>
                      </div>
                      <p className="text-xs text-slate-400 text-center">Secure payment via Stripe. Your card details are never stored on this site.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---------- CALENDAR / LIST ----------
  const monthLabel = viewDate.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  const calFeedUrl = `${API_URL}/api/public/events/${siteSlug}/calendar.ics`;

  return (
    <div className={embedParams.isEmbed ? 'bg-slate-50' : 'min-h-screen bg-slate-50'}>
      <Helmet><title>Events — {tenantName}</title></Helmet>
      <div className={embedParams.isEmbed ? 'max-w-5xl mx-auto px-4 py-4' : 'max-w-5xl mx-auto px-4 py-10'}>
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div>
            {!embedParams.isEmbed && (
              <Link to={`/site/${siteSlug}`} className="text-sm text-slate-400 hover:text-slate-600">{tenantName}</Link>
            )}
            <h1 className="text-4xl font-bold text-slate-900 flex items-center gap-3 mt-1"><CalendarDays className="w-8 h-8 text-indigo-600" /> Events</h1>
          </div>
          <div className="flex items-center gap-2">
            <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5">
              <button onClick={() => setMode('calendar')} className={`px-3 py-1.5 text-sm rounded-md ${mode === 'calendar' ? 'bg-indigo-600 text-white' : 'text-slate-600'}`} data-testid="public-view-calendar">Calendar</button>
              <button onClick={() => setMode('list')} className={`px-3 py-1.5 text-sm rounded-md ${mode === 'list' ? 'bg-indigo-600 text-white' : 'text-slate-600'}`} data-testid="public-view-list">List</button>
            </div>
            {embedParams.showIcal && (
              <a href={calFeedUrl} className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm text-slate-700 hover:bg-slate-50" data-testid="public-subscribe-ical">
                <Download className="w-4 h-4" /> iCal
              </a>
            )}
            {embedParams.showAbsence && data?.absentee_enabled !== false && (
              <button onClick={() => { setAbsDone(false); setAbsOpen(true); }} className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-900 text-white text-sm hover:bg-slate-800" data-testid="public-report-absence-btn">
                <UserX className="w-4 h-4" /> Report an absence
              </button>
            )}
          </div>
        </div>

        {/* Filter Bar */}
        {embedParams.showFilters && (
        <div className="bg-white rounded-2xl border border-slate-200 p-4 mb-6 space-y-3" data-testid="events-filter-bar">

          {/* Teacher / Calendar pills — shown when there are iCal sources */}
          {icalSources.length > 0 && (
            <div className="flex flex-wrap gap-2 items-center" data-testid="filter-calendar-pills">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide mr-1">Calendar:</span>
              <button
                onClick={() => setFilterSource('')}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border transition-all ${
                  !filterSource
                    ? 'bg-slate-900 text-white border-slate-900'
                    : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
                }`}
                data-testid="filter-calendar-all"
              >
                All calendars
              </button>
              {icalSources.map(src => (
                <button
                  key={src.source_id}
                  onClick={() => setFilterSource(filterSource === src.source_id ? '' : src.source_id)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border transition-all ${
                    filterSource === src.source_id
                      ? 'text-white border-transparent'
                      : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'
                  }`}
                  style={filterSource === src.source_id ? { backgroundColor: src.color, borderColor: src.color } : {}}
                  data-testid={`filter-calendar-${src.source_id}`}
                >
                  <span
                    className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: src.color }}
                  />
                  {src.name}
                </button>
              ))}
            </div>
          )}

          <div className="flex flex-wrap gap-3 items-end">
            {/* Search */}
            <div className="relative flex-1 min-w-[160px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              <input
                value={filterSearch}
                onChange={(e) => setFilterSearch(e.target.value)}
                placeholder="Search events…"
                className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-300"
                data-testid="filter-search-input"
              />
            </div>

            {/* Event Type */}
            {filterOptions.event_types.length > 0 && (
              <div className="min-w-[160px]">
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="w-full py-2 pl-3 pr-8 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-300 bg-white"
                  data-testid="filter-type-select"
                >
                  <option value="">All event types</option>
                  {filterOptions.event_types.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            )}

            {/* Department */}
            {filterOptions.departments.length > 0 && (
              <div className="min-w-[160px]">
                <select
                  value={filterDept}
                  onChange={(e) => setFilterDept(e.target.value)}
                  className="w-full py-2 pl-3 pr-8 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-300 bg-white"
                  data-testid="filter-dept-select"
                >
                  <option value="">All departments</option>
                  {filterOptions.departments.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
            )}

            {activeFilterCount > 0 && (
              <button
                onClick={() => { setFilterType(''); setFilterDept(''); setFilterSearch(''); setFilterSource(''); }}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 text-sm text-slate-600 hover:bg-slate-50"
                data-testid="filter-clear-btn"
              >
                <X className="w-3.5 h-3.5" /> Clear
              </button>
            )}
          </div>

          {/* Active filter chips */}
          {activeFilterCount > 0 && (
            <div className="flex flex-wrap gap-2" data-testid="active-filter-chips">
              {filterSource && (() => {
                const src = icalSources.find(s => s.source_id === filterSource);
                return src ? (
                  <span className="inline-flex items-center gap-1.5 text-xs px-3 py-1 rounded-full border text-white" style={{ backgroundColor: src.color, borderColor: src.color }}>
                    <span className="w-2 h-2 rounded-full bg-white/60 flex-shrink-0" />
                    {src.name}
                    <button onClick={() => setFilterSource('')} className="hover:opacity-75 ml-0.5">×</button>
                  </span>
                ) : null;
              })()}
              {filterSearch && (
                <span className="inline-flex items-center gap-1.5 bg-indigo-50 text-indigo-700 text-xs px-3 py-1 rounded-full border border-indigo-100">
                  <Search className="w-3 h-3" /> "{filterSearch}"
                  <button onClick={() => setFilterSearch('')} className="hover:text-indigo-900 ml-0.5">×</button>
                </span>
              )}
              {filterType && (
                <span className="inline-flex items-center gap-1.5 bg-indigo-50 text-indigo-700 text-xs px-3 py-1 rounded-full border border-indigo-100">
                  <Tag className="w-3 h-3" /> {filterType}
                  <button onClick={() => setFilterType('')} className="hover:text-indigo-900 ml-0.5">×</button>
                </span>
              )}
              {filterDept && (
                <span className="inline-flex items-center gap-1.5 bg-indigo-50 text-indigo-700 text-xs px-3 py-1 rounded-full border border-indigo-100">
                  <Filter className="w-3 h-3" /> {filterDept}
                  <button onClick={() => setFilterDept('')} className="hover:text-indigo-900 ml-0.5">×</button>
                </span>
              )}
              <span className="text-xs text-slate-400 self-center">
                {filterSource ? (() => {
                  const src = icalSources.find(s => s.source_id === filterSource);
                  return <span className="text-sm text-slate-500">{filteredEvents.length} event{filteredEvents.length !== 1 ? 's' : ''} from <strong>{src?.name || 'this calendar'}</strong></span>;
                })() : (
                  <span className="text-sm text-slate-500">{filteredEvents.length} event{filteredEvents.length !== 1 ? 's' : ''} shown</span>
                )}
              </span>
            </div>
          )}
        </div>
        )}

        {filteredEvents.length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-200 p-16 text-center text-slate-400">
            <CalendarIcon className="w-12 h-12 mx-auto mb-4 opacity-40" />
            {events.length === 0
              ? 'No upcoming events right now. Check back soon.'
              : 'No events match your current filters.'}
            {activeFilterCount > 0 && (
              <button onClick={() => { setFilterType(''); setFilterDept(''); setFilterSearch(''); setFilterSource(''); }} className="block mx-auto mt-3 text-indigo-600 text-sm hover:underline">Clear filters</button>
            )}
          </div>
        ) : mode === 'calendar' ? (
          <div className="bg-white rounded-2xl border border-slate-200 p-4">
            <div className="flex items-center justify-center gap-2 mb-4">
              <button onClick={() => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1))} className="p-2 rounded-lg hover:bg-slate-100" data-testid="public-cal-prev"><ChevronLeft className="w-5 h-5" /></button>
              <h2 className="text-lg font-semibold w-48 text-center">{monthLabel}</h2>
              <button onClick={() => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1))} className="p-2 rounded-lg hover:bg-slate-100" data-testid="public-cal-next"><ChevronRight className="w-5 h-5" /></button>
            </div>
            <div className="grid grid-cols-7 gap-px bg-slate-200 rounded-lg overflow-hidden border border-slate-200">
              {WEEKDAYS.map(w => <div key={w} className="bg-slate-50 text-center text-xs font-semibold text-slate-500 py-2">{w}</div>)}
              {calendarCells.map((cell, i) => {
                const dayEvents = cell ? (eventsByDay[cell.toDateString()] || []) : [];
                const isToday = cell && cell.toDateString() === new Date().toDateString();
                return (
                  <div key={i} className={`bg-white min-h-[96px] p-1.5 ${!cell ? 'bg-slate-50/40' : ''}`}>
                    {cell && (
                      <>
                        <div className={`text-xs font-medium mb-1 ${isToday ? 'bg-indigo-600 text-white rounded-full w-6 h-6 flex items-center justify-center' : 'text-slate-500'}`}>{cell.getDate()}</div>
                        <div className="space-y-1">
                          {dayEvents.map(ev => (
                            <button key={ev.event_id} onClick={() => navigate(`/site/${siteSlug}/events/${ev.event_id}`)}
                              className="w-full text-left text-[11px] leading-tight px-1.5 py-0.5 rounded truncate text-white hover:opacity-90"
                              style={{ backgroundColor: ev.color || '#6366f1' }} title={ev.title}
                              data-testid={`public-event-${ev.event_id}`}>
                              {ev.title}
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredEvents.map(ev => (
              <button key={ev.event_id} onClick={() => navigate(`/site/${siteSlug}/events/${ev.event_id}`)}
                className="w-full text-left bg-white rounded-xl border border-slate-200 p-5 flex items-center gap-4 hover:shadow-md transition-shadow"
                data-testid={`public-event-row-${ev.event_id}`}>
                <div className="flex flex-col items-center justify-center w-16 shrink-0">
                  <span className="text-xs uppercase text-slate-400">{new Date(ev.start).toLocaleDateString(undefined, { month: 'short' })}</span>
                  <span className="text-2xl font-bold text-slate-900">{new Date(ev.start).getDate()}</span>
                </div>
                <div className="w-1 self-stretch rounded-full" style={{ backgroundColor: ev.color || '#6366f1' }} />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-slate-900">{ev.title}</p>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500 mt-1">
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{ev.all_day ? 'All day' : new Date(ev.start).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                    {ev.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{ev.location}</span>}
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {ev.ical_source_name && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full text-white font-medium" style={{ backgroundColor: ev.color || '#6366f1' }} data-testid="event-source-badge">
                        {ev.ical_source_name}
                      </span>
                    )}
                    {ev.event_type && <span className="bg-slate-100 text-slate-600 text-[10px] px-2 py-0.5 rounded-full">{ev.event_type}</span>}
                    {(ev.departments || []).map(d => <span key={d} className="bg-indigo-50 text-indigo-600 text-[10px] px-2 py-0.5 rounded-full">{d}</span>)}
                  </div>
                  {ev.description_html && <p className="text-sm text-slate-400 mt-1 line-clamp-1">{stripHtml(ev.description_html).slice(0, 120)}</p>}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {absOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setAbsOpen(false)}>
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="absence-modal">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2"><UserX className="w-5 h-5 text-slate-500" /> Report an absence</h3>
              <button onClick={() => setAbsOpen(false)} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
            </div>
            {absDone ? (
              <div className="p-8 text-center">
                <div className="w-14 h-14 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto mb-4"><CalendarDays className="w-7 h-7" /></div>
                <p className="font-semibold text-slate-900">Absence reported</p>
                <p className="text-sm text-slate-500 mt-1">The office has been notified. Thank you.</p>
                <button onClick={() => setAbsOpen(false)} className="mt-5 px-4 py-2 rounded-lg bg-slate-900 text-white text-sm">Close</button>
              </div>
            ) : (
              <div className="p-6 space-y-4">
                <div>
                  <label className="text-sm font-medium text-slate-700">Student name *</label>
                  <input value={absForm.student_name} onChange={(e) => setAbsForm(f => ({ ...f, student_name: e.target.value }))} className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="absence-student-input" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium text-slate-700">Your name</label>
                    <input value={absForm.parent_name} onChange={(e) => setAbsForm(f => ({ ...f, parent_name: e.target.value }))} className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="absence-parent-input" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700">Your email</label>
                    <input type="email" value={absForm.parent_email} onChange={(e) => setAbsForm(f => ({ ...f, parent_email: e.target.value }))} className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="absence-email-input" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium text-slate-700">From *</label>
                    <input type="date" value={absForm.date_from} onChange={(e) => setAbsForm(f => ({ ...f, date_from: e.target.value }))} className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="absence-from-input" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700">To</label>
                    <input type="date" value={absForm.date_to} onChange={(e) => setAbsForm(f => ({ ...f, date_to: e.target.value }))} className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="absence-to-input" />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Reason *</label>
                  <input value={absForm.reason} onChange={(e) => setAbsForm(f => ({ ...f, reason: e.target.value }))} placeholder="Illness, appointment…" className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="absence-reason-input" />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Notes</label>
                  <textarea rows={3} value={absForm.notes} onChange={(e) => setAbsForm(f => ({ ...f, notes: e.target.value }))} className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="absence-notes-input" />
                </div>
                <button onClick={submitAbsence} disabled={absSubmitting} className="w-full py-2.5 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-60" data-testid="absence-submit-btn">
                  {absSubmitting ? 'Submitting…' : 'Submit absence report'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicEventsPage;
